import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiWithAuth from '../../services/apiWithAuth';
import ConfirmModal from '../common/ConfirmModal';
import './ShopDetailsPage.css';

const ShopDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState(null);

  // Confirmation modal state for status changes
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null,
    title: '',
    message: ''
  });

  // Fetch shop details
  const fetchShopDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiWithAuth.get(`/admin-panel/shops/${id}`);
      
      if (response.data.success) {
        setShop(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch shop details');
      }
    } catch (err) {
      console.error('Error fetching shop details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch shop details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch shop analytics
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await apiWithAuth.get(`/admin-panel/shops/${id}/analytics`);
      
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Handle shop status change
  const handleStatusChange = (action) => {
    const actionConfig = {
      approve: {
        title: `Approve shop "${shop.name}"?`,
        message: 'This will approve the shop and make it active for customers.',
        action: 'APPROVED'
      },
      reject: {
        title: `Reject shop "${shop.name}"?`,
        message: 'This will reject the shop request. The shop owner will be notified.',
        action: 'REJECTED'
      },
      hold: {
        title: `Put shop "${shop.name}" on hold?`,
        message: 'This will put the shop request on hold for further review.',
        action: 'HOLD'
      }
    };

    const config = actionConfig[action];
    if (config) {
      setConfirmModal({
        open: true,
        action: config.action,
        title: config.title,
        message: config.message
      });
    }
  };

  // Confirm status change
  const confirmStatusChange = async () => {
    if (!confirmModal.action) return;

    setLoading(true);
    try {
      const response = await apiWithAuth.patch(
        `/admin-panel/shops/${id}/status`,
        { status: confirmModal.action }
      );

      if (response.data.success) {
        // Update the shop status
        setShop(prev => ({ ...prev, status: confirmModal.action }));
        setConfirmModal({ open: false, action: null, title: '', message: '' });
      } else {
        throw new Error(response.data.message || 'Failed to update shop status');
      }
    } catch (err) {
      console.error('Error updating shop status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update shop status');
    } finally {
      setLoading(false);
    }
  };

  const cancelStatusChange = () => {
    setConfirmModal({ open: false, action: null, title: '', message: '' });
  };

  useEffect(() => {
    fetchShopDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) {
      fetchAnalytics();
    }
  }, [activeTab, analytics, id]);

  if (loading) {
    return (
      <div className="shop-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading shop details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shop-details-page">
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">!</span>
            {error}
          </div>
          <button onClick={() => navigate('/shops')} className="btn-back">
            Back to Shops
          </button>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="shop-details-page">
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">×</span>
            Shop not found
          </div>
          <button onClick={() => navigate('/shops')} className="btn-back">
            Back to Shops
          </button>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="shop-overview">
      {/* Hero Section */}
      <div className="shop-hero-section">
        <div className="shop-hero-content">
          <div className="shop-logo-container">
            <img 
              src={shop.logoUrl || '/KD-icon.png'} 
              alt={shop.name}
              className="shop-logo-hero"
              onError={(e) => { e.target.src = '/KD-icon.png'; }}
            />
          </div>
          <div className="shop-hero-info">
            <h1 className="shop-name-hero">{shop.name}</h1>
            <p className="shop-description-hero">{shop.description || 'No description available'}</p>
            <div className="shop-badges-hero">
              <span className={`status-badge ${shop.status?.toLowerCase()}`}>
                {shop.status}
              </span>
              <span className={`verification-badge ${shop.isVerified ? 'verified' : 'unverified'}`}>
                {shop.isVerified ? 'Verified' : 'Unverified'}
              </span>
              <span className={`active-badge ${shop.shopActive ? 'active' : 'inactive'}`}>
                {shop.shopActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
        <div className="shop-quick-stats">
          <div className="quick-stat">
            <span className="stat-number">⭐ {shop.rating?.toFixed(1) || 'N/A'}</span>
            <span className="stat-label">Rating ({shop.ratingCount || 0} reviews)</span>
          </div>
          <div className="quick-stat">
            <span className="stat-number">{shop.followersCount || 0}</span>
            <span className="stat-label">Followers</span>
          </div>
          <div className="quick-stat">
            <span className="stat-number">{shop.address?.city || 'N/A'}</span>
            <span className="stat-label">Location</span>
          </div>
        </div>
      </div>

      <div className="shop-details-grid">
        {/* Owner Information */}
        <div className="detail-section">
          <h3>Owner Information</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Name:</span>
              <span className="value">{shop.owner?.fullName || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Email:</span>
              <span className="value">{shop.owner?.email || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Phone:</span>
              <span className="value">{shop.owner?.phone || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Plan:</span>
              <span className="value">{shop.owner?.planType || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="detail-section">
          <h3>Address</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Building:</span>
              <span className="value">{shop.address?.buildingNo || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Street:</span>
              <span className="value">{shop.address?.street || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">City:</span>
              <span className="value">{shop.address?.city || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">State:</span>
              <span className="value">{shop.address?.state || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Pincode:</span>
              <span className="value">{shop.address?.pincode || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="detail-section">
          <h3>Contact Details</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Contact Phone:</span>
              <span className="value">{shop.contactPhone || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Shop Phone:</span>
              <span className="value">{shop.shopPhone || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Contact Email:</span>
              <span className="value">{shop.contactEmail || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="detail-section">
          <h3>Business Details</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Seller Type:</span>
              <span className="value">{shop.sellerType || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">GST:</span>
              <span className="value">{shop.gst || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Opening Time:</span>
              <span className="value">{shop.openingTime || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Closing Time:</span>
              <span className="value">{shop.closingTime || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Business Days:</span>
              <span className="value">{shop.businessDays?.join(', ') || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Rating & Engagement */}
        <div className="detail-section">
          <h3>Rating & Engagement</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Rating:</span>
              <span className="value">{shop.rating?.toFixed(1) || 'N/A'} ({shop.ratingCount || 0} reviews)</span>
            </div>
            <div className="detail-item">
              <span className="label">Followers:</span>
              <span className="value">{shop.followersCount || 0}</span>
            </div>
            <div className="detail-item">
              <span className="label">Follow Requests:</span>
              <span className="value">{shop.followRequestsCount || 0}</span>
            </div>
          </div>
        </div>

        {/* Delivery Settings */}
        <div className="detail-section">
          <h3>Delivery Settings</h3>
          <div className="detail-items">
            <div className="detail-item">
              <span className="label">Base Delivery Fee:</span>
              <span className="value">₹{shop.baseDeliveryFee || 0}</span>
            </div>
            <div className="detail-item">
              <span className="label">Base Distance:</span>
              <span className="value">{shop.baseDeliveryDistanceKm || 0} km</span>
            </div>
            <div className="detail-item">
              <span className="label">Per KM Fee:</span>
              <span className="value">₹{shop.perKmDeliveryFee || 0}</span>
            </div>
            <div className="detail-item">
              <span className="label">Max Distance:</span>
              <span className="value">{shop.maxDeliveryDistanceKm || 0} km</span>
            </div>
            <div className="detail-item">
              <span className="label">Free Delivery Min:</span>
              <span className="value">₹{shop.freeDeliveryMinOrderValue || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      {shop.categories && shop.categories.length > 0 && (
        <div className="categories-section">
          <h3 className="section-title">Shop Categories</h3>
          <div className="categories-list">
            {shop.categories.map(category => (
              <div key={category._id} className="category-tag">
                <span className="category-name">{category.name}</span>
                <span className="category-level">Level {category.level}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shop Images Gallery */}
      {shop.shopImages && shop.shopImages.length > 0 && (
        <div className="gallery-section">
          <h3 className="section-title">Shop Gallery</h3>
          <div className="shop-images-grid">
            {shop.shopImages.map((image, index) => (
              <img 
                key={index} 
                src={image} 
                alt={`Shop ${index + 1}`}
                className="shop-image"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Shop Status Actions */}
      {shop.status !== 'APPROVED' && shop.status !== 'REJECTED' && (
        <div className="status-actions">
          <button 
            className="status-btn approve-btn"
            onClick={() => handleStatusChange('approve')}
          >
            ✓ Approve
          </button>
          <button 
            className="status-btn reject-btn"
            onClick={() => handleStatusChange('reject')}
          >
            ✗ Reject
          </button>
          <button 
            className="status-btn hold-btn"
            onClick={() => handleStatusChange('hold')}
          >
            ⏸ Hold
          </button>
        </div>
      )}

      {(shop.status === 'APPROVED' || shop.status === 'REJECTED') && (
        <div className="status-final">
          <span className={`final-status ${shop.status.toLowerCase()}`}>
            {shop.status === 'APPROVED' ? '✓ Shop Approved' : '✗ Shop Rejected'}
          </span>
        </div>
      )}
    </div>
  );

  const renderShopMembers = () => (
    <div className="shop-staff-tab">
      <div className="shop-staff-header">
        <h3>Shop Members</h3>
        <p>Team members assigned to this shop</p>
      </div>
      {(shop.shopMembers || []).length === 0 ? (
        <div className="empty-state">
          <p>No shop members assigned to this shop.</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Position</th>
                <th>Permissions</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {(shop.shopMembers || []).map((member) => (
                <tr key={member._id}>
                  <td>
                    <div className="staff-user-cell">
                      <img
                        src={member.user?.profileImageUrl || '/KD-icon.png'}
                        alt={member.user?.fullName || 'Member'}
                        className="staff-avatar"
                        onError={(e) => { e.target.src = '/KD-icon.png'; }}
                      />
                      <span>{member.user?.fullName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{member.user?.phone || 'N/A'}</td>
                  <td>{member.user?.email || 'N/A'}</td>
                  <td>{member.position || '—'}</td>
                  <td>
                    <div className="permission-chips">
                      {member.canAddProduct && <span className="status-badge status-pending">Product</span>}
                      {member.canAddVideo && <span className="status-badge status-pending">Video</span>}
                      {member.canAddCoupon && <span className="status-badge status-pending">Coupon</span>}
                      {member.canBookOrder && <span className="status-badge status-pending">Book Order</span>}
                      {!member.canAddProduct && !member.canAddVideo && !member.canAddCoupon && !member.canBookOrder && (
                        <span className="text-muted">None</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${member.accountStatus === 'ACTIVE' ? 'status-approved' : 'status-rejected'}`}>
                      {member.accountStatus || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {member.joiningDate
                      ? new Date(member.joiningDate).toLocaleDateString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderDeliveryBoys = () => (
    <div className="shop-staff-tab">
      <div className="shop-staff-header">
        <h3>Delivery Boys</h3>
        <p>Delivery partners assigned to this shop</p>
      </div>
      {(shop.deliveryBoys || []).length === 0 ? (
        <div className="empty-state">
          <p>No delivery boys assigned to this shop.</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Delivery Boy</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Position</th>
                <th>Skills</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {(shop.deliveryBoys || []).map((boy) => (
                <tr key={boy._id}>
                  <td>
                    <div className="staff-user-cell">
                      <img
                        src={boy.user?.profileImageUrl || '/KD-icon.png'}
                        alt={boy.user?.fullName || 'Delivery boy'}
                        className="staff-avatar"
                        onError={(e) => { e.target.src = '/KD-icon.png'; }}
                      />
                      <span>{boy.user?.fullName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{boy.user?.phone || 'N/A'}</td>
                  <td>{boy.user?.email || 'N/A'}</td>
                  <td>{boy.position || '—'}</td>
                  <td>
                    {(boy.skills || []).length > 0
                      ? boy.skills.join(', ')
                      : '—'}
                  </td>
                  <td>
                    <span className={`status-badge ${boy.accountStatus === 'ACTIVE' ? 'status-approved' : 'status-rejected'}`}>
                      {boy.accountStatus || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {boy.joiningDate
                      ? new Date(boy.joiningDate).toLocaleDateString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderFieldBoys = () => (
    <div className="shop-staff-tab">
      <div className="shop-staff-header">
        <h3>Field Boy</h3>
        <p>Field partner assigned to this shop</p>
      </div>
      {(shop.fieldBoys || []).length === 0 ? (
        <div className="empty-state">
          <p>No field boy assigned to this shop.</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Field Boy</th>
                <th>Code</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Zone</th>
                <th>Skills</th>
                <th>Status</th>
                <th>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {(shop.fieldBoys || []).map((boy) => (
                <tr key={boy._id}>
                  <td>
                    <div className="staff-user-cell">
                      <img
                        src={boy.user?.profileImageUrl || '/KD-icon.png'}
                        alt={boy.user?.fullName || 'Field boy'}
                        className="staff-avatar"
                        onError={(e) => { e.target.src = '/KD-icon.png'; }}
                      />
                      <span>{boy.user?.fullName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{boy.employeeCode || '—'}</td>
                  <td>{boy.user?.phone || 'N/A'}</td>
                  <td>{boy.user?.email || 'N/A'}</td>
                  <td>{boy.zone || '—'}</td>
                  <td>
                    {(boy.skills || []).length > 0
                      ? boy.skills.join(', ')
                      : '—'}
                  </td>
                  <td>
                    <span className={`status-badge ${['ACTIVE', 'ACCEPTED'].includes(boy.accountStatus) ? 'status-approved' : 'status-rejected'}`}>
                      {boy.accountStatus || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {boy.assignedAt
                      ? new Date(boy.assignedAt).toLocaleDateString()
                      : boy.joiningDate
                        ? new Date(boy.joiningDate).toLocaleDateString()
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderBloggers = () => (
    <div className="shop-staff-tab">
      <div className="shop-staff-header">
        <h3>Blogger</h3>
        <p>Blogger assigned to this shop</p>
      </div>
      {(shop.bloggers || []).length === 0 ? (
        <div className="empty-state">
          <p>No blogger assigned to this shop.</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Blogger</th>
                <th>Code</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Zone</th>
                <th>Niches</th>
                <th>Status</th>
                <th>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {(shop.bloggers || []).map((blogger) => (
                <tr key={blogger._id}>
                  <td>
                    <div className="staff-user-cell">
                      <img
                        src={blogger.user?.profileImageUrl || '/KD-icon.png'}
                        alt={blogger.user?.fullName || 'Blogger'}
                        className="staff-avatar"
                        onError={(e) => { e.target.src = '/KD-icon.png'; }}
                      />
                      <span>{blogger.user?.fullName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{blogger.employeeCode || '—'}</td>
                  <td>{blogger.user?.phone || 'N/A'}</td>
                  <td>{blogger.user?.email || 'N/A'}</td>
                  <td>{blogger.zone || '—'}</td>
                  <td>
                    {(blogger.niches || []).length > 0
                      ? blogger.niches.join(', ')
                      : '—'}
                  </td>
                  <td>
                    <span className={`status-badge ${['ACTIVE', 'ACCEPTED'].includes(blogger.accountStatus) ? 'status-approved' : 'status-rejected'}`}>
                      {blogger.accountStatus || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {blogger.assignedAt
                      ? new Date(blogger.assignedAt).toLocaleDateString()
                      : blogger.joiningDate
                        ? new Date(blogger.joiningDate).toLocaleDateString()
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => {
    if (loadingAnalytics) {
      return (
        <div className="loading-analytics">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      );
    }

    if (!analytics) {
      return (
        <div className="no-analytics">
          <p>No analytics data available</p>
        </div>
      );
    }

    // Calculate percentages for visual bars
    const productActivePercentage = analytics.products.totalProducts > 0 
      ? (analytics.products.activeProducts / analytics.products.totalProducts) * 100 
      : 0;
    
    const orderCompletionRate = analytics.orders.totalOrders > 0 
      ? (analytics.orders.completedOrders / analytics.orders.totalOrders) * 100 
      : 0;

    const customerActiveRate = analytics.customers.totalCustomers > 0 
      ? (analytics.customers.activeCustomers / analytics.customers.totalCustomers) * 100 
      : 0;

    return (
      <div className="shop-analytics">
        {/* Key Metrics Overview */}
        <div className="analytics-overview">
          <div className="metric-card revenue">
            <div className="metric-icon">₹</div>
            <div className="metric-content">
              <div className="metric-value">₹{analytics.revenue.totalRevenue?.toLocaleString() || 0}</div>
              <div className="metric-label">Total Revenue</div>
              <div className="metric-sub">Avg: ₹{analytics.revenue.avgOrderValue?.toFixed(0) || 0} per order</div>
            </div>
          </div>
          
          <div className="metric-card orders">
            <div className="metric-icon">📦</div>
            <div className="metric-content">
              <div className="metric-value">{analytics.orders.totalOrders}</div>
              <div className="metric-label">Total Orders</div>
              <div className="metric-sub">{orderCompletionRate.toFixed(1)}% completion rate</div>
            </div>
          </div>
          
          <div className="metric-card customers">
            <div className="metric-icon">👥</div>
            <div className="metric-content">
              <div className="metric-value">{analytics.customers.totalCustomers}</div>
              <div className="metric-label">Total Customers</div>
              <div className="metric-sub">{analytics.followers} followers</div>
            </div>
          </div>
          
          <div className="metric-card products">
            <div className="metric-icon">📋</div>
            <div className="metric-content">
              <div className="metric-value">{analytics.products.totalProducts}</div>
              <div className="metric-label">Total Products</div>
              <div className="metric-sub">{productActivePercentage.toFixed(1)}% active</div>
            </div>
          </div>
        </div>

        {/* Detailed Analytics */}
        <div className="analytics-detailed">
          {/* Products Analytics */}
          <div className="analytics-section">
            <h3>Product Analytics</h3>
            <div className="chart-container">
              <div className="chart-item">
                <div className="chart-label">Active Products</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{width: `${productActivePercentage}%`}}></div>
                </div>
                <div className="chart-value">{analytics.products.activeProducts} / {analytics.products.totalProducts}</div>
              </div>
              <div className="chart-item">
                <div className="chart-label">Approved Products</div>
                <div className="progress-bar">
                  <div className="progress-fill approved" style={{width: `${analytics.products.totalProducts > 0 ? (analytics.products.approvedProducts / analytics.products.totalProducts) * 100 : 0}%`}}></div>
                </div>
                <div className="chart-value">{analytics.products.approvedProducts} / {analytics.products.totalProducts}</div>
              </div>
              <div className="stat-highlight">
                <span className="stat-number">₹{analytics.products.avgPrice?.toFixed(0) || 0}</span>
                <span className="stat-desc">Average Product Price</span>
              </div>
            </div>
          </div>

          {/* Orders Analytics */}
          <div className="analytics-section">
            <h3>Order Analytics</h3>
            <div className="chart-container">
              <div className="order-breakdown">
                <div className="order-stat completed">
                  <div className="order-count">{analytics.orders.completedOrders}</div>
                  <div className="order-label">Completed</div>
                </div>
                <div className="order-stat pending">
                  <div className="order-count">{analytics.orders.pendingOrders}</div>
                  <div className="order-label">Pending</div>
                </div>
                <div className="order-stat cancelled">
                  <div className="order-count">{analytics.orders.cancelledOrders}</div>
                  <div className="order-label">Cancelled</div>
                </div>
              </div>
              <div className="completion-rate">
                <div className="rate-circle">
                  <div className="rate-number">{orderCompletionRate.toFixed(1)}%</div>
                  <div className="rate-label">Success Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Analytics */}
          <div className="analytics-section">
            <h3>Customer Analytics</h3>
            <div className="chart-container">
              <div className="customer-metrics">
                <div className="customer-metric">
                  <div className="metric-bar">
                    <div className="bar-fill" style={{height: `${customerActiveRate}%`}}></div>
                  </div>
                  <div className="metric-info">
                    <div className="metric-num">{analytics.customers.activeCustomers}</div>
                    <div className="metric-text">Active</div>
                  </div>
                </div>
                <div className="customer-metric">
                  <div className="metric-bar">
                    <div className="bar-fill followers" style={{height: `${analytics.customers.totalCustomers > 0 ? (analytics.followers / analytics.customers.totalCustomers) * 100 : 0}%`}}></div>
                  </div>
                  <div className="metric-info">
                    <div className="metric-num">{analytics.followers}</div>
                    <div className="metric-text">Followers</div>
                  </div>
                </div>
                <div className="customer-metric">
                  <div className="metric-bar">
                    <div className="bar-fill reviews" style={{height: `${analytics.ratings.totalReviews > 0 ? Math.min((analytics.ratings.totalReviews / analytics.customers.totalCustomers) * 100, 100) : 0}%`}}></div>
                  </div>
                  <div className="metric-info">
                    <div className="metric-num">{analytics.ratings.totalReviews}</div>
                    <div className="metric-text">Reviews</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="shop-details-page">
      <div className="shop-details-header">
        <button onClick={() => navigate('/shops')} className="btn-back">
          ← Back to Shops
        </button>
        <h1>Shop Details</h1>
      </div>

      <div className="shop-details-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'shop-members' ? 'active' : ''}`}
          onClick={() => setActiveTab('shop-members')}
        >
          Shop Members
          {(shop.shopMembersCount ?? shop.shopMembers?.length ?? 0) > 0 && (
            <span className="tab-count">{shop.shopMembersCount ?? shop.shopMembers?.length ?? 0}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'delivery-boys' ? 'active' : ''}`}
          onClick={() => setActiveTab('delivery-boys')}
        >
          Delivery Boys
          {(shop.deliveryBoysCount ?? shop.deliveryBoys?.length ?? 0) > 0 && (
            <span className="tab-count">{shop.deliveryBoysCount ?? shop.deliveryBoys?.length ?? 0}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'field-boys' ? 'active' : ''}`}
          onClick={() => setActiveTab('field-boys')}
        >
          Field Boy
          {(shop.fieldBoysCount ?? shop.fieldBoys?.length ?? 0) > 0 && (
            <span className="tab-count">{shop.fieldBoysCount ?? shop.fieldBoys?.length ?? 0}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'bloggers' ? 'active' : ''}`}
          onClick={() => setActiveTab('bloggers')}
        >
          Blogger
          {(shop.bloggersCount ?? shop.bloggers?.length ?? 0) > 0 && (
            <span className="tab-count">{shop.bloggersCount ?? shop.bloggers?.length ?? 0}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      <div className="shop-details-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'shop-members' && renderShopMembers()}
        {activeTab === 'delivery-boys' && renderDeliveryBoys()}
        {activeTab === 'field-boys' && renderFieldBoys()}
        {activeTab === 'bloggers' && renderBloggers()}
        {activeTab === 'analytics' && renderAnalytics()}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={loading}
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
      />
    </div>
  );
};

export default ShopDetailsPage;